/**
 * Registro_Acciones_Semanticas (semantic action registry) — variante
 * **demo** client-side de la Capa Operacional AI-First.
 *
 * Implementa la interfaz {@link Registry} de `../api/contracts`. Cubre dos
 * tareas del plan:
 *
 *   - **Tarea 6.1** — catálogo declarativo y versionado con round-trip
 *     exacto `declare`/`get` (R12.2, Property 30) y clasificación de
 *     criticidad (R18.1, R20.3, Property 48).
 *   - **Tarea 6.4** — el **ejecutor** con gate de permisos/políticas y
 *     human-in-the-loop (HITL):
 *       · `invoke(id, params, actor)` aplica el gate RBAC + políticas
 *         ANTES de ejecutar; las críticas nunca se auto-ejecutan, quedan
 *         PENDIENTES de aprobación humana (R12.4–R12.7, R18.2, R18.3,
 *         R20.1, R20.3);
 *       · `approve(solicitud_id, aprobador)` exige permiso RBAC del
 *         aprobador y registra la aprobación ANTES de ejecutar (R18.4,
 *         R18.5);
 *       · `revert(evento_accion_id, actor)` revierte solo acciones
 *         reversibles, registrando la reversión (R12.6, R20.2);
 *       · las solicitudes críticas pendientes expiran sin aprobación en
 *         una ventana configurable (default 15 min) (R18.6).
 *
 * **Seguridad no negociable (R18.3, Property 46):** no existe ninguna ruta
 * de código por la que `invoke` ejecute automáticamente una
 * Accion_Critica_Hidraulica, con independencia de score, confianza o
 * umbral. La única vía de ejecución de una crítica es {@link
 * RegistroAccionesLocal.approve} por un humano con el permiso RBAC
 * requerido.
 *
 * Determinismo y testabilidad: la evaluación de permisos
 * ({@link evaluarPermisos}) y la decisión de criticidad
 * ({@link esAccionCritica}) son **funciones puras**. La expiración no
 * depende de `setTimeout`: usa un **reloj inyectable** (`reloj`) y un
 * chequeo por timestamp ({@link RegistroAccionesLocal.expirarPendientes}),
 * por lo que los tests pueden avanzar el tiempo de forma controlada.
 */
import type {
  AccionDeclarada,
  EntidadRef,
  EstadoSolicitud,
  EventStore,
  Id,
  IsoTimestamp,
  ParamsAccion,
  Registry,
  ResultadoAccion,
  Result,
  Rol,
  TipoEntidad,
} from "../api/contracts";
import { runtime } from "../api/runtime";
import { CATALOGO_ACCIONES, CATALOGO_VERSION } from "./actionCatalog";

// ---------------------------------------------------------------------------
// Configuración por defecto
// ---------------------------------------------------------------------------

/** Ventana de validez por defecto de una solicitud crítica pendiente (R18.6): 15 min. */
export const VENTANA_CRITICA_MS_DEFAULT = 15 * 60 * 1000;

// ---------------------------------------------------------------------------
// Copia profunda determinista (garantía de round-trip sin aliasing)
// ---------------------------------------------------------------------------

/**
 * Clona una {@link AccionDeclarada} en profundidad para que ni el
 * catálogo ni los llamadores compartan referencias mutables con el
 * almacén interno. Esto garantiza el round-trip exacto de Property 30:
 * mutar la entrada o la salida nunca altera lo almacenado.
 */
function clonarAccion(a: AccionDeclarada): AccionDeclarada {
  return {
    id: a.id,
    nombre: a.nombre,
    parametros: a.parametros.map((p) => ({
      nombre: p.nombre,
      tipo: p.tipo,
      requerido: p.requerido,
    })),
    permisos_requeridos: [...a.permisos_requeridos],
    reversible: a.reversible,
    operacion_inversa: a.operacion_inversa,
    criticidad: a.criticidad,
    es_critica_hidraulica: a.es_critica_hidraulica,
  };
}

// ---------------------------------------------------------------------------
// Clasificación crítica (R18.1, R20.3)
// ---------------------------------------------------------------------------

/**
 * **Función pura** que decide si una Accion_Semantica debe tratarse como
 * Accion_Critica_Hidraulica que requiere aprobación humana explícita
 * (Property 48):
 *
 *   - **R18.1** — la acción está marcada `es_critica_hidraulica` (apertura/
 *     cierre de Valvula, setpoint de PRV, eliminación de evidencia); o
 *   - **R20.3** — la acción es **no reversible**; o
 *   - **R20.3** — la acción es de **criticidad elevada** (`alta`).
 *
 * Se exporta para que el gate HITL y los tests la reutilicen sin
 * instanciar el Registro.
 */
export function esAccionCritica(a: AccionDeclarada): boolean {
  return a.es_critica_hidraulica || !a.reversible || a.criticidad === "alta";
}

// ---------------------------------------------------------------------------
// Gate de permisos / políticas (R12.5, R12.7) — funciones puras
// ---------------------------------------------------------------------------

/**
 * Proveedor del conjunto de roles RBAC que posee un actor (humano o IA).
 * Es **inyectable** para desacoplar el ejecutor de la fuente de identidad
 * (AuthContext en la UI, generador adversario en los tests).
 */
export type ProveedorPermisos = (actor: string) => readonly Rol[];

/**
 * Resultado de evaluar una política operacional. `permitido=false`
 * bloquea la ejecución con el `motivo` registrado en la auditoría.
 */
export interface DecisionPolitica {
  permitido: boolean;
  motivo?: string;
}

/**
 * Evaluador de políticas vigentes (policy-aware, R12.5). Función pura del
 * `(actor, accion, params)` a una {@link DecisionPolitica}. Por defecto
 * todo está permitido; el gate RBAC es la restricción primaria.
 */
export type EvaluadorPoliticas = (
  actor: string,
  accion: AccionDeclarada,
  params: ParamsAccion,
) => DecisionPolitica;

/** Conjunto de roles RBAC válidos de la fundación (para el proveedor por defecto). */
const ROLES_VALIDOS: readonly Rol[] = [
  "Operador",
  "Analista_NRW",
  "Inspector",
  "Supervisor",
  "Despachador",
  "Operador_Terreno",
];

/**
 * **Función pura** del gate de permisos (R12.5, R12.7, Property 33): el
 * actor está autorizado si posee **al menos uno** de los roles requeridos
 * por la acción. Una acción sin permisos requeridos se considera abierta.
 */
export function evaluarPermisos(
  rolesActor: readonly Rol[],
  accion: AccionDeclarada,
): boolean {
  if (accion.permisos_requeridos.length === 0) return true;
  return accion.permisos_requeridos.some((r) => rolesActor.includes(r));
}

/**
 * Proveedor de permisos por defecto para la demo: interpreta el `actor`
 * como una lista de roles separados por coma o espacio (ej.
 * `"Supervisor"` o `"u-12#Supervisor,Despachador"`), conservando solo los
 * {@link Rol} válidos. Los actores que no portan un rol válido (p.ej. la
 * IA: `"copilot"`, `"ia"`) no obtienen ningún permiso, de modo que el
 * gate RBAC los deniega. Es **determinista** y no realiza E/S.
 */
export function proveedorPermisosPorDefecto(actor: string): readonly Rol[] {
  const tokens = actor.split(/[#,\s]+/).map((t) => t.trim());
  return ROLES_VALIDOS.filter((r) => tokens.includes(r));
}

/** Política por defecto: no añade restricciones más allá del gate RBAC. */
export function politicaPermisivaPorDefecto(): DecisionPolitica {
  return { permitido: true };
}

// ---------------------------------------------------------------------------
// Errores y utilidades
// ---------------------------------------------------------------------------

/** Mensaje de error para una acción no declarada (R20.1). */
export function errorAccionNoDeclarada(id: Id): string {
  return `Accion_Semantica '${id}' no declarada en el Registro_Acciones_Semanticas.`;
}

/** Instante actual en ISO-8601 UTC a partir de un instante en milisegundos. */
function isoDe(ms: number): IsoTimestamp {
  return new Date(ms).toISOString();
}

/** Genera un identificador único (UUID si está disponible). */
function generarId(prefijo: string): Id {
  try {
    const c = (globalThis as { crypto?: Crypto }).crypto;
    if (c && typeof c.randomUUID === "function") return `${prefijo}-${c.randomUUID()}`;
  } catch {
    /* fallback abajo */
  }
  return `${prefijo}-${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Mapa nombre-de-parámetro → tipo de entidad afectada, para etiquetar el
 * Evento_Operacional con la entidad sobre la que actúa la Accion_Semantica
 * (define la `seq` por entidad en el Event_Store).
 */
const PARAM_TIPO_ENTIDAD: Record<string, TipoEntidad> = {
  valvula_ref: "Valvula",
  prv_ref: "PRV",
  cuadrilla_ref: "Cuadrilla",
  orden_ref: "Orden_Inspeccion",
  dma_ref: "DMA",
  device_ref: "Medidor",
  evidencia_ref: "Anomalia",
};

/**
 * Resuelve la entidad afectada por una invocación a partir de sus
 * parámetros. Recorre los parámetros declarados en orden y usa el primero
 * cuyo nombre mapea a un {@link TipoEntidad}; si ninguno aplica, etiqueta
 * el evento contra la propia acción como entidad de tipo DMA (fallback
 * seguro para la cadena hash).
 */
function entidadAfectada(accion: AccionDeclarada, params: ParamsAccion): EntidadRef {
  for (const p of accion.parametros) {
    const tipo = PARAM_TIPO_ENTIDAD[p.nombre];
    const valor = params[p.nombre];
    if (tipo && (typeof valor === "string" || typeof valor === "number")) {
      return { tipo, id: String(valor) };
    }
  }
  // Fallback: la acción no referencia una entidad de dominio conocida.
  return { tipo: "DMA", id: `accion:${accion.id}` };
}

// ---------------------------------------------------------------------------
// Solicitudes pendientes (HITL) y registro de ejecuciones (para revert)
// ---------------------------------------------------------------------------

/** Solicitud de Accion_Critica_Hidraulica en espera de aprobación humana (R18.2). */
export interface SolicitudPendiente {
  solicitud_id: Id;
  accion_id: Id;
  params: ParamsAccion;
  /** Actor (humano o IA) que originó la invocación. */
  solicitante: string;
  estado: EstadoSolicitud;
  /** Instante de creación (ms epoch). */
  creada_ms: number;
  /** Instante de expiración (ms epoch); pasado este punto sin aprobar, expira (R18.6). */
  expira_ms: number;
}

/** Registro interno de una ejecución, base para la reversión (R20.2). */
interface EjecucionRegistrada {
  accion_id: Id;
  params: ParamsAccion;
  actor: string;
  entidad: EntidadRef;
}

// ---------------------------------------------------------------------------
// Opciones de construcción del Registro
// ---------------------------------------------------------------------------

/** Opciones del {@link RegistroAccionesLocal}. */
export interface RegistroAccionesLocalOptions {
  /** Event_Store donde registrar invocaciones, denegaciones y reversiones (R12.4). */
  eventStore?: EventStore;
  /** Proveedor de roles RBAC del actor (R12.5). */
  proveedorPermisos?: ProveedorPermisos;
  /** Evaluador de políticas vigentes (R12.5). */
  politicas?: EvaluadorPoliticas;
  /** Reloj inyectable en ms epoch (testabilidad de la expiración, R18.6). */
  reloj?: () => number;
  /** Ventana de validez de una solicitud crítica pendiente en ms (default 15 min). */
  ventanaCriticaMs?: number;
}

// ---------------------------------------------------------------------------
// Implementación demo del Registro
// ---------------------------------------------------------------------------

/**
 * Registro_Acciones_Semanticas en memoria con ejecutor HITL. Carga el
 * catálogo versionado, soporta declaraciones adicionales con round-trip
 * exacto y aplica el gate de permisos/políticas y la garantía maestra de
 * human-in-the-loop en `invoke`/`approve`/`revert`.
 */
export class RegistroAccionesLocal implements Registry {
  /** Almacén interno por id; las entradas son copias profundas. */
  private readonly catalogo = new Map<Id, AccionDeclarada>();

  /** Solicitudes críticas pendientes/resueltas por id (HITL, R18.2). */
  private readonly solicitudes = new Map<Id, SolicitudPendiente>();

  /** Ejecuciones registradas indexadas por `event_id`, para la reversión (R20.2). */
  private readonly ejecuciones = new Map<Id, EjecucionRegistrada>();

  /** Versión del catálogo cargado (R12.2). */
  readonly version: string = CATALOGO_VERSION;

  private readonly eventStoreInyectado?: EventStore;
  private readonly proveedorPermisos: ProveedorPermisos;
  private readonly politicas: EvaluadorPoliticas;
  private readonly reloj: () => number;
  private readonly ventanaCriticaMs: number;

  /**
   * Construye el Registro a partir de un catálogo declarativo. Por
   * defecto carga {@link CATALOGO_ACCIONES}; los tests pueden inyectar un
   * catálogo propio y/o las dependencias del ejecutor vía `opts`.
   */
  constructor(
    catalogoInicial: readonly AccionDeclarada[] = CATALOGO_ACCIONES,
    opts: RegistroAccionesLocalOptions = {},
  ) {
    for (const a of catalogoInicial) {
      this.catalogo.set(a.id, clonarAccion(a));
    }
    this.eventStoreInyectado = opts.eventStore;
    this.proveedorPermisos = opts.proveedorPermisos ?? proveedorPermisosPorDefecto;
    this.politicas = opts.politicas ?? politicaPermisivaPorDefecto;
    this.reloj = opts.reloj ?? (() => Date.now());
    this.ventanaCriticaMs = opts.ventanaCriticaMs ?? VENTANA_CRITICA_MS_DEFAULT;
  }

  // -------------------------------------------------------------------------
  // Catálogo (tarea 6.1)
  // -------------------------------------------------------------------------

  /**
   * Declara (o re-declara) una Accion_Semantica en el catálogo (R12.2).
   * Almacena una copia profunda para preservar el round-trip exacto.
   */
  declare(a: AccionDeclarada): Result<void> {
    if (!a || typeof a.id !== "string" || a.id.length === 0) {
      return { ok: false, error: "La Accion_Semantica debe declarar un 'id' no vacío." };
    }
    this.catalogo.set(a.id, clonarAccion(a));
    return { ok: true, value: undefined };
  }

  /**
   * Recupera la declaración de una Accion_Semantica con round-trip
   * exacto (R12.2, Property 30). Devuelve una copia profunda para que el
   * llamador no pueda mutar el almacén interno.
   */
  get(id: Id): Result<AccionDeclarada> {
    const a = this.catalogo.get(id);
    if (!a) {
      return { ok: false, error: errorAccionNoDeclarada(id) };
    }
    return { ok: true, value: clonarAccion(a) };
  }

  /** Lista todas las acciones declaradas (copias profundas). */
  list(): AccionDeclarada[] {
    return [...this.catalogo.values()].map(clonarAccion);
  }

  /**
   * Clasificación efectiva como Accion_Critica_Hidraulica (R18.1 ∪
   * R20.3, Property 48). Devuelve `false` si la acción no está declarada.
   */
  esCritica(id: Id): boolean {
    const a = this.catalogo.get(id);
    return a ? esAccionCritica(a) : false;
  }

  // -------------------------------------------------------------------------
  // Ejecutor con gate de permisos/políticas y HITL (tarea 6.4)
  // -------------------------------------------------------------------------

  /** Resuelve el Event_Store efectivo: inyectado o el del runtime DEMO. */
  private es(): EventStore {
    return this.eventStoreInyectado ?? runtime.eventStore();
  }

  /**
   * Registra un Evento_Operacional de la frontera de acción y devuelve el
   * `event_id` asignado (o `null` si el append degradó). Nunca lanza: un
   * fallo de registro no debe romper la frontera de servicio.
   */
  private async registrarEvento(
    tipo: string,
    entidad: EntidadRef,
    actor: string,
    payload: Record<string, unknown>,
  ): Promise<Id | null> {
    try {
      const r = await this.es().append({
        tipo,
        entidad_ref: entidad,
        actor,
        ts: isoDe(this.reloj()),
        payload,
        correlation_id: generarId("corr"),
      });
      return r.ok ? r.value.event_id : null;
    } catch {
      return null;
    }
  }

  /**
   * Invoca una Accion_Semantica aplicando el gate de seguridad (R12.4–
   * R12.7, R18.2, R18.3, R20.1, R20.3):
   *
   *   1. **No declarada → rechazo** (R20.1): no ejecuta, registra el
   *      intento bloqueado y devuelve error.
   *   2. **Gate de permisos/políticas ANTES de ejecutar** (R12.5): si el
   *      actor carece del permiso requerido o una política prohíbe la
   *      acción, **no ejecuta**, preserva el estado, registra el intento
   *      denegado como Evento_Operacional y devuelve error (R12.7).
   *   3. **Crítica → PENDIENTE** (R18.2, R18.3, R20.3): si la acción es
   *      crítica/no reversible/criticidad alta, **nunca** se auto-ejecuta;
   *      se crea una solicitud PENDIENTE que requiere aprobación humana.
   *   4. **Permitida y no crítica → ejecuta** (R12.4): registra el
   *      Evento_Operacional con params, actor, resultado éxito y, si es
   *      reversible, la referencia de reversibilidad.
   */
  async invoke(id: Id, params: ParamsAccion, actor: string): Promise<Result<ResultadoAccion>> {
    // (1) Acción no declarada: rechazo + auditoría (R20.1).
    const accion = this.catalogo.get(id);
    if (!accion) {
      await this.registrarEvento(
        "accion_no_declarada",
        { tipo: "DMA", id: `accion:${id}` },
        actor,
        { accion_id: id, resultado: "rechazado", motivo: "accion_no_declarada" },
      );
      return { ok: false, error: errorAccionNoDeclarada(id) };
    }

    const entidad = entidadAfectada(accion, params);

    // (2) Gate de permisos + políticas ANTES de cualquier efecto (R12.5, R12.7).
    const roles = this.proveedorPermisos(actor);
    const permitidoRbac = evaluarPermisos(roles, accion);
    const decisionPolitica = this.politicas(actor, accion, params);
    if (!permitidoRbac || !decisionPolitica.permitido) {
      const motivo = !permitidoRbac
        ? "permiso_insuficiente"
        : `politica_prohibida: ${decisionPolitica.motivo ?? "no especificada"}`;
      // El estado se preserva sin cambios; el intento queda auditado (R12.7).
      await this.registrarEvento("accion_denegada", entidad, actor, {
        accion_id: id,
        params,
        resultado: "denegado",
        motivo,
      });
      return {
        ok: false,
        error: !permitidoRbac
          ? `Accion_Semantica '${id}' denegada: el actor no posee el permiso RBAC requerido (${accion.permisos_requeridos.join(", ")}).`
          : `Accion_Semantica '${id}' denegada por política vigente: ${decisionPolitica.motivo ?? "prohibida"}.`,
        degraded: false,
      };
    }

    // (3) Crítica: NUNCA auto-ejecuta; crea solicitud PENDIENTE (R18.2, R18.3, R20.3).
    if (esAccionCritica(accion)) {
      const ahora = this.reloj();
      const solicitud_id = generarId("sol");
      const solicitud: SolicitudPendiente = {
        solicitud_id,
        accion_id: id,
        params,
        solicitante: actor,
        estado: "pendiente",
        creada_ms: ahora,
        expira_ms: ahora + this.ventanaCriticaMs,
      };
      this.solicitudes.set(solicitud_id, solicitud);
      const evento_id = await this.registrarEvento("solicitud_pendiente", entidad, actor, {
        accion_id: id,
        solicitud_id,
        params,
        es_critica_hidraulica: accion.es_critica_hidraulica,
        reversible: accion.reversible,
        criticidad: accion.criticidad,
        expira_ts: isoDe(solicitud.expira_ms),
        resultado: "pendiente",
        motivo: "requiere_aprobacion_humana",
      });
      return {
        ok: true,
        value: {
          estado: "pendiente",
          solicitud_id,
          ...(evento_id ? { evento_ref: { tipo: entidad.tipo, id: evento_id } } : {}),
          detalle:
            "Accion_Critica_Hidraulica en estado pendiente: requiere aprobación humana explícita (R18.2).",
        },
      };
    }

    // (4) Permitida y no crítica: ejecuta y registra (R12.4).
    return this.ejecutar(accion, params, actor, entidad, {
      via: "invoke",
      solicitante: actor,
    });
  }

  /**
   * Aprueba una solicitud PENDIENTE de Accion_Critica_Hidraulica (R18.4,
   * R18.5):
   *
   *   - solicitud inexistente → error;
   *   - solicitud expirada → la expira y rechaza (R18.6);
   *   - aprobador **sin** permiso RBAC → deniega, **mantiene pendiente** y
   *     registra el intento (R18.5);
   *   - aprobador con permiso → registra como Evento_Operacional la
   *     identidad del aprobador, la justificación, los parámetros y el
   *     timestamp **antes** de ejecutar, y luego ejecuta (R18.4).
   */
  async approve(solicitud_id: Id, aprobador: string): Promise<Result<ResultadoAccion>> {
    const solicitud = this.solicitudes.get(solicitud_id);
    if (!solicitud) {
      return { ok: false, error: `Solicitud '${solicitud_id}' no encontrada.` };
    }

    // Expiración perezosa por timestamp (R18.6): nunca ejecuta una caducada.
    if (solicitud.estado === "pendiente" && this.reloj() >= solicitud.expira_ms) {
      await this.expirarSolicitud(solicitud);
    }

    if (solicitud.estado !== "pendiente") {
      return {
        ok: false,
        error: `La solicitud '${solicitud_id}' no está pendiente (estado: ${solicitud.estado}).`,
      };
    }

    const accion = this.catalogo.get(solicitud.accion_id);
    if (!accion) {
      return { ok: false, error: errorAccionNoDeclarada(solicitud.accion_id) };
    }

    const entidad = entidadAfectada(accion, solicitud.params);

    // Gate RBAC del aprobador: sin permiso → deniega y mantiene pendiente (R18.5).
    const roles = this.proveedorPermisos(aprobador);
    if (!evaluarPermisos(roles, accion)) {
      await this.registrarEvento("aprobacion_denegada", entidad, aprobador, {
        accion_id: accion.id,
        solicitud_id,
        resultado: "denegado",
        motivo: "permiso_insuficiente",
      });
      return {
        ok: false,
        error: `Aprobación denegada: el aprobador no posee el permiso RBAC requerido (${accion.permisos_requeridos.join(", ")}). La solicitud sigue pendiente.`,
        degraded: false,
      };
    }

    // Registro de la aprobación ANTES de ejecutar (R18.4): aprobador,
    // justificación, parámetros y timestamp como Evento_Operacional.
    await this.registrarEvento("accion_aprobada", entidad, aprobador, {
      accion_id: accion.id,
      solicitud_id,
      aprobador,
      justificacion: "Aprobación humana explícita de Accion_Critica_Hidraulica (R18.4).",
      params: solicitud.params,
      ts: isoDe(this.reloj()),
    });

    solicitud.estado = "aprobada";

    // Ejecuta tras la aprobación humana.
    const resultado = await this.ejecutar(accion, solicitud.params, aprobador, entidad, {
      via: "approve",
      solicitante: solicitud.solicitante,
      solicitud_id,
    });
    if (resultado.ok) {
      solicitud.estado = "ejecutada";
    }
    return resultado;
  }

  /**
   * Revierte una acción ejecutada **solo si es reversible** (R12.6,
   * R20.2). Registra la reversión como Evento_Operacional referenciando la
   * operación inversa declarada. Una acción no reversible o un evento
   * desconocido → error sin efecto.
   */
  async revert(evento_accion_id: Id, actor: string): Promise<Result<ResultadoAccion>> {
    const ejecucion = this.ejecuciones.get(evento_accion_id);
    if (!ejecucion) {
      return {
        ok: false,
        error: `No existe una ejecución registrada con evento '${evento_accion_id}' para revertir.`,
      };
    }
    const accion = this.catalogo.get(ejecucion.accion_id);
    if (!accion) {
      return { ok: false, error: errorAccionNoDeclarada(ejecucion.accion_id) };
    }
    if (!accion.reversible) {
      return {
        ok: false,
        error: `La Accion_Semantica '${accion.id}' no es reversible; no puede revertirse (R20.2).`,
      };
    }

    const evento_id = await this.registrarEvento(
      "accion_revertida",
      ejecucion.entidad,
      actor,
      {
        accion_id: accion.id,
        evento_accion_id,
        operacion_inversa: accion.operacion_inversa,
        params: ejecucion.params,
        resultado: "revertida",
      },
    );

    return {
      ok: true,
      value: {
        estado: "revertida",
        ...(evento_id ? { evento_ref: { tipo: ejecucion.entidad.tipo, id: evento_id } } : {}),
        detalle: accion.operacion_inversa
          ? `Reversión registrada vía operación inversa '${accion.operacion_inversa}' (R20.2).`
          : "Reversión registrada (acción sin efecto físico que deshacer).",
      },
    };
  }

  // -------------------------------------------------------------------------
  // Expiración de solicitudes pendientes (R18.6) — testeable por timestamp
  // -------------------------------------------------------------------------

  /**
   * Expira **todas** las solicitudes críticas pendientes cuya ventana de
   * validez ya venció según el reloj inyectado (R18.6). No ejecuta ninguna
   * acción y registra la expiración como Evento_Operacional. Devuelve los
   * ids expirados. Diseñada para invocarse desde un chequeo periódico o
   * desde los tests tras avanzar el reloj, **sin** depender de `setTimeout`.
   */
  async expirarPendientes(): Promise<Id[]> {
    const ahora = this.reloj();
    const expirados: Id[] = [];
    for (const solicitud of this.solicitudes.values()) {
      if (solicitud.estado === "pendiente" && ahora >= solicitud.expira_ms) {
        await this.expirarSolicitud(solicitud);
        expirados.push(solicitud.solicitud_id);
      }
    }
    return expirados;
  }

  /** Marca una solicitud como expirada y registra el Evento_Operacional (R18.6). */
  private async expirarSolicitud(solicitud: SolicitudPendiente): Promise<void> {
    solicitud.estado = "expirada";
    const accion = this.catalogo.get(solicitud.accion_id);
    const entidad = accion
      ? entidadAfectada(accion, solicitud.params)
      : { tipo: "DMA" as TipoEntidad, id: `accion:${solicitud.accion_id}` };
    await this.registrarEvento("solicitud_expirada", entidad, solicitud.solicitante, {
      accion_id: solicitud.accion_id,
      solicitud_id: solicitud.solicitud_id,
      resultado: "expirada",
      motivo: "sin_aprobacion_en_ventana",
      expira_ts: isoDe(solicitud.expira_ms),
    });
  }

  // -------------------------------------------------------------------------
  // Ejecución efectiva (registro completo, R12.4)
  // -------------------------------------------------------------------------

  /**
   * Ejecuta una acción autorizada y registra el Evento_Operacional con
   * params, actor, resultado (éxito) y, cuando es reversible, la
   * referencia de reversibilidad (R12.4, Property 31). En la demo la
   * "ejecución" se materializa como el propio Evento_Operacional en el
   * Event_Store append-only (no hay mutación física de la red).
   */
  private async ejecutar(
    accion: AccionDeclarada,
    params: ParamsAccion,
    actor: string,
    entidad: EntidadRef,
    ctx: { via: "invoke" | "approve"; solicitante: string; solicitud_id?: Id },
  ): Promise<Result<ResultadoAccion>> {
    const evento_id = await this.registrarEvento("accion_ejecutada", entidad, actor, {
      accion_id: accion.id,
      params,
      actor,
      solicitante: ctx.solicitante,
      via: ctx.via,
      ...(ctx.solicitud_id ? { solicitud_id: ctx.solicitud_id } : {}),
      resultado: "exito",
      reversible: accion.reversible,
      // Referencia de reversibilidad cuando aplica (R12.4, R12.6).
      operacion_inversa: accion.reversible ? accion.operacion_inversa : null,
    });

    if (evento_id) {
      // Indexa la ejecución para habilitar la reversión por event_id (R20.2).
      this.ejecuciones.set(evento_id, {
        accion_id: accion.id,
        params,
        actor,
        entidad,
      });
    }

    return {
      ok: true,
      value: {
        estado: "ejecutada",
        ...(evento_id ? { evento_ref: { tipo: entidad.tipo, id: evento_id } } : {}),
        detalle: `Accion_Semantica '${accion.id}' ejecutada por '${actor}'.`,
      },
    };
  }

  // -------------------------------------------------------------------------
  // Accesores de diagnóstico / UI / test
  // -------------------------------------------------------------------------

  /** Recupera una solicitud por id (UI/test). Devuelve copia superficial. */
  getSolicitud(solicitud_id: Id): SolicitudPendiente | undefined {
    const s = this.solicitudes.get(solicitud_id);
    return s ? { ...s, params: { ...s.params } } : undefined;
  }

  /** Lista las solicitudes en estado pendiente (UI/test). */
  solicitudesPendientes(): SolicitudPendiente[] {
    return [...this.solicitudes.values()]
      .filter((s) => s.estado === "pendiente")
      .map((s) => ({ ...s, params: { ...s.params } }));
  }
}

/** Factoría del Registro demo cargado con el catálogo versionado. */
export function crearRegistroAcciones(
  catalogoInicial: readonly AccionDeclarada[] = CATALOGO_ACCIONES,
  opts: RegistroAccionesLocalOptions = {},
): RegistroAccionesLocal {
  return new RegistroAccionesLocal(catalogoInicial, opts);
}
